import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs.toFixed(0)}s`
}

export function generateSeed(): number {
  return Math.floor(Math.random() * 1000000)
}

// Build Pollinations image URL
export function buildImageUrl(
  prompt: string,
  width: number,
  height: number,
  seed: number,
  enhance: boolean = true
): string {
  const encodedPrompt = encodeURIComponent(prompt)
  return `https://image.pollinations.ai/prompt/${encodedPrompt}?width=${width}&height=${height}&seed=${seed}&nologo=true&enhance=${enhance}&nojson=true`
}

// Gallery image URL builder
export function buildGalleryUrl(
  prompt: string,
  ratio: string,
  style: string,
  seed: number
): string {
  const [w, h] = ratio.split(':').map(Number)
  const baseSize = 512
  const width = w >= h ? baseSize : Math.round(baseSize * w / h)
  const height = h >= w ? baseSize : Math.round(baseSize * h / w)

  let enhancedPrompt = prompt
  if (style) {
    enhancedPrompt += `, ${style} style`
  }

  return buildImageUrl(enhancedPrompt, width, height, seed, false)
}

export const ASPECT_RATIOS = [
  { label: "Square", value: "1:1", w: 1024, h: 1024 },
  { label: "Landscape", value: "16:9", w: 1024, h: 576 },
  { label: "Portrait", value: "9:16", w: 576, h: 1024 },
  { label: "Standard", value: "4:3", w: 1024, h: 768 },
  { label: "Portrait 3:4", value: "3:4", w: 768, h: 1024 },
  { label: "Ultrawide", value: "21:9", w: 1024, h: 439 },
] as const

export const IMAGE_STYLES = [
  { label: "Default", value: "" },
  { label: "Photorealistic", value: "photorealistic" },
  { label: "Anime / Manga", value: "anime" },
  { label: "Digital Art", value: "digital-art" },
  { label: "Oil Painting", value: "oil-painting" },
  { label: "Watercolor", value: "watercolor" },
  { label: "3D Render", value: "3d-render" },
  { label: "Pencil Sketch", value: "sketch" },
  { label: "Cyberpunk", value: "cyberpunk" },
  { label: "Fantasy Art", value: "fantasy" },
  { label: "Minimalist", value: "minimalist" },
  { label: "Vintage / Retro", value: "vintage" },
] as const

export const QUALITY_LEVELS = [
  { label: "Fast (Draft)", value: "fast" },
  { label: "Standard", value: "standard" },
  { label: "High Quality", value: "high" },
  { label: "Ultra HD", value: "ultra" },
] as const

export const AI_MODELS = [
  { label: "FLUX (Recommended)", value: "flux" },
  { label: "Stable Diffusion XL", value: "sdxl" },
  { label: "Turbo (Fast)", value: "turbo" },
] as const

export const VIDEO_MODELS = [
  { label: "Seedance (Free Tier)", value: "seedance", provider: "seedance" },
  { label: "Luma Dream Machine", value: "luma", provider: "luma" },
  { label: "Kling 3.0", value: "kling", provider: "kling" },
  { label: "Runway Gen-4", value: "runway", provider: "runway" },
] as const

export const VIDEO_STYLES = [
  { label: "Cinematic", value: "cinematic" },
  { label: "Anime", value: "anime" },
  { label: "3D Animation", value: "3d-animation" },
  { label: "Documentary", value: "documentary" },
  { label: "Music Video", value: "music-video" },
  { label: "Product Showcase", value: "product" },
] as const

export const RANDOM_PROMPTS = [
  "A serene Japanese garden with cherry blossoms in full bloom, a small wooden bridge over a koi pond, soft morning light filtering through the trees, watercolor style",
  "A futuristic neon-lit cyberpunk cityscape at night, flying cars, holographic advertisements, rain-soaked streets reflecting colorful lights, cinematic 8k",
  "A majestic dragon soaring through stormy clouds, lightning illuminating its iridescent scales, epic fantasy art, highly detailed, dramatic composition",
  "A cozy cottage in the Scottish highlands, surrounded by purple heather, misty mountains in the background, warm light from the windows, oil painting style",
  "An astronaut floating in space near a colorful nebula, Earth visible in the distance, stars scattered across the void, photorealistic, NASA style",
  "A mystical forest with bioluminescent plants and mushrooms, a winding path leading to an ancient stone archway, ethereal glow, fantasy art",
  "A steampunk airship sailing through golden clouds at sunset, intricate brass details, gears and pipes visible, Victorian era aesthetic, highly detailed",
  "A tranquil beach at sunset with crystal clear turquoise water, palm trees swaying in the breeze, a wooden hammock between two trees, tropical paradise",
  "A medieval castle perched on a cliff overlooking the ocean, dramatic stormy sky, waves crashing against the rocks below, cinematic lighting, epic scale",
  "A cute robot tending to a garden of mechanical flowers, soft pastel colors, whimsical style, children's book illustration, heartwarming scene",
] as const

export const GALLERY_ITEMS = [
  { prompt: "A futuristic city with neon lights", ratio: "16:9", style: "cyberpunk", type: "image" as const },
  { prompt: "Magical forest with glowing mushrooms", ratio: "1:1", style: "fantasy", type: "image" as const },
  { prompt: "Portrait of a wise old wizard", ratio: "3:4", style: "digital-art", type: "image" as const },
  { prompt: "Sunset over mountain peaks", ratio: "16:9", style: "photorealistic", type: "image" as const },
  { prompt: "Cute anime character in a cafe", ratio: "3:4", style: "anime", type: "image" as const },
  { prompt: "Abstract geometric patterns", ratio: "1:1", style: "minimalist", type: "image" as const },
  { prompt: "Ocean waves crashing on rocks", ratio: "16:9", style: "photorealistic", type: "video" as const },
  { prompt: "Butterfly emerging from cocoon", ratio: "9:16", style: "cinematic", type: "video" as const },
] as const

export const STYLE_ENHANCEMENTS: Record<string, string> = {
  photorealistic: "photorealistic, highly detailed, 8k, professional photography",
  anime: "anime style, manga art, vibrant colors, detailed line art",
  "digital-art": "digital art, concept art, detailed illustration, vibrant colors",
  "oil-painting": "oil painting, classical art style, rich textures, museum quality",
  watercolor: "watercolor painting, soft edges, artistic, flowing colors",
  "3d-render": "3D render, octane render, blender, cinematic lighting, ray tracing",
  sketch: "pencil sketch, hand drawn, graphite art, detailed shading",
  cyberpunk: "cyberpunk style, neon lights, futuristic, dystopian, high tech",
  fantasy: "fantasy art, magical, ethereal, otherworldly, detailed",
  minimalist: "minimalist design, clean lines, simple, elegant, modern",
  vintage: "vintage style, retro aesthetic, film grain, nostalgic",
}

export interface ScriptTemplateDef {
  id: string
  name: string
  category: string
  description: string
  template: string
  variables: string[]
}

export const SCRIPT_TEMPLATES: ScriptTemplateDef[] = [
  {
    id: "story",
    name: "Short Story",
    category: "Creative Writing",
    description: "Generate a compelling short story with characters, plot, and setting",
    template: "Write a short story about {topic}. Include vivid descriptions, character development, and a satisfying conclusion. Style: {style}. Length: approximately {length} words.",
    variables: ["topic", "style", "length"],
  },
  {
    id: "prompt",
    name: "AI Image Prompt",
    category: "AI Tools",
    description: "Generate optimized prompts for AI image generation",
    template: "Create a detailed AI image generation prompt for: {subject}. Style: {artStyle}. Mood: {mood}. Include lighting, camera angle, and atmosphere details.",
    variables: ["subject", "artStyle", "mood"],
  },
  {
    id: "script",
    name: "Video Script",
    category: "Video Production",
    description: "Generate a professional video script with scenes and dialogue",
    template: "Write a {duration}-second video script about {topic}. Format: Scene descriptions with camera directions. Tone: {tone}. Target audience: {audience}.",
    variables: ["topic", "duration", "tone", "audience"],
  },
  {
    id: "dialogue",
    name: "Character Dialogue",
    category: "Creative Writing",
    description: "Generate natural dialogue between characters",
    template: "Write a dialogue between {character1} and {character2} about {topic}. Setting: {setting}. Tone: {tone}. Include natural pauses, emotions, and body language descriptions.",
    variables: ["character1", "character2", "topic", "setting", "tone"],
  },
  {
    id: "marketing",
    name: "Marketing Copy",
    category: "Business",
    description: "Generate persuasive marketing copy",
    template: "Write marketing copy for {product}. Target audience: {audience}. Key benefits: {benefits}. Tone: {tone}. Include a compelling headline and call-to-action.",
    variables: ["product", "audience", "benefits", "tone"],
  },
  {
    id: "social",
    name: "Social Media Post",
    category: "Social Media",
    description: "Generate engaging social media content",
    template: "Write a {platform} post about {topic}. Tone: {tone}. Include relevant hashtags and emojis. Length: {length} characters max.",
    variables: ["platform", "topic", "tone", "length"],
  },
]

export const TTS_LANGUAGES = [
  { code: "en-US", name: "English (US)" },
  { code: "en-GB", name: "English (UK)" },
  { code: "es-ES", name: "Spanish" },
  { code: "fr-FR", name: "French" },
  { code: "de-DE", name: "German" },
  { code: "it-IT", name: "Italian" },
  { code: "ja-JP", name: "Japanese" },
  { code: "ko-KR", name: "Korean" },
  { code: "zh-CN", name: "Chinese (Simplified)" },
  { code: "zh-TW", name: "Chinese (Traditional)" },
  { code: "ru-RU", name: "Russian" },
  { code: "pt-BR", name: "Portuguese (Brazil)" },
  { code: "ar-SA", name: "Arabic" },
  { code: "hi-IN", name: "Hindi" },
  { code: "th-TH", name: "Thai" },
  { code: "vi-VN", name: "Vietnamese" },
  { code: "tr-TR", name: "Turkish" },
  { code: "pl-PL", name: "Polish" },
  { code: "nl-NL", name: "Dutch" },
  { code: "sv-SE", name: "Swedish" },
] as const
